import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import numeral from 'numeral';

interface ShiftVDPFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    shift_plans: any[];
    form: any;
    full_form: any;
    schema: any;
    full_schema: any;
    storages: any[];
    targetForDelete: any[];
    equipments: any[];

    meta: any;
}

const state: ShiftVDPFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    shift_plans: [],
    storages: [],
    targetForDelete: [],
    equipments: [],
    form: null,
    full_form: null,
    schema: null,
    full_schema: null,

    meta: {},
};

const initState: ShiftVDPFormState = cloneDeep(state);

export const SHIFT_VDP_DATA = 'GET_SHIFT_VDP_DATA';
export const SHIFT_VDP_FULL_DATA = 'GET_SHIFT_VDP_FULL_DATA';
export const SHIFT_EQUIPMENT_DATA = 'GET_EQUIPMENT_DATA';
export const SHIFT_VDP_SCHEMA = 'GET_SHIFT_VDP_SCHEMA';
export const SHIFT_VDP_FULL_SCHEMA = 'GET_SHIFT_VDP_FULL_SCHEMA';
export const SHIFT_VDP_STORAGES = 'SHIFT_VDP_STORAGES';

export const actions: ActionTree<ShiftVDPFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SHIFT_VDP_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(SHIFT_VDP_FULL_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.full_id
                    }),
                vm.$q(SHIFT_EQUIPMENT_DATA)
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'spve.id',
                            equipment_id: 'spve.equipment_id',
                        },
                        from: 's:shift_plan_vdp_equipments',
                        from_as: 'spve',
                        where: {
                            $eq: ['spve.plan_id', {$: params.id}]
                        }
                    }),
            ]).then((result: ServerResult) => {
                const _res = result.getOperation(SHIFT_VDP_DATA).getContent()
                const _res_full = result.getOperation(SHIFT_VDP_FULL_DATA).getContent()
                const _res_equipments = result.getOperation(SHIFT_EQUIPMENT_DATA).getContent()
                commit('SET_FORM_DATA', {
                    document: _res.document,
                    equipments: _res_equipments.documents,
                    schema: _res.schema,
                    full_document: _res_full.document,
                    full_schema: _res_full.schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SHIFT_VDP_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:shift_plan_vdp`
                    }),
                vm.$q(SHIFT_VDP_FULL_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:shift_full_plan_vdp`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    schema: result.getOperation(SHIFT_VDP_SCHEMA).getContent().schema,
                    full_schema: result.getOperation(SHIFT_VDP_FULL_SCHEMA).getContent().schema,
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        let normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('SHIFT_VDP_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('shift_plan_vdp_equipments', 'plan_id', (createRelated: any) => {
                      return state.equipments.filter((equip: any) => equip.active && !equip.id).map((equip: any) => createRelated({
                        plan_id: documentId,
                        equipment_id: equip.equipment_id
                      }));
                    })
                    .deleteManyRelation('shift_plan_vdp_equipments', 'plan_id', (deleteRelated: any) => {
                      return state.equipments.filter((equip: any) => !equip.active && equip.id).map((equip: any) => deleteRelated({
                        id: equip.id,
                      }));
                    })
            } else {
                saveQuery = vm.$q('SHIFT_VDP_DATA_SAVE')
                    .create('s:shift_plan_vdp', normalizedForm)
                    .withManyRelation('shift_plan_vdp_equipments', 'plan_id', (createRelated: Function) => {
                        return state.equipments
                            .map((equip: any) => createRelated({ 
                                plan_id: documentId,
                                equipment_id: equip.equipment_id
                            }));
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('SHIFT_VDP_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    state.full_form.plan_id = operationResult.getContent().document.id
                    state.full_form.number = operationResult.getContent().document.number
                    normalizedForm = prepareFormObjectForSave(state.full_schema, state.full_form);
                    if (state.full_form.id) {
                        saveQuery = vm.$q('SHIFT_VDP_DATA_SAVE')
                        .update(state.full_form.id, normalizedForm)
                    } else {
                        saveQuery = vm.$q('SHIFT_VDP_DATA_SAVE')
                        .create('s:shift_full_plan_vdp', normalizedForm)
                    }
                    vm.$db([
                        saveQuery
                    ]).then(() => {
                        vm.$db([
                          vm.$q('CHECK_NEEW_WASHING')
                            .raw('documents.list')
                            .action({
                                columns: {
                                    equipment_id: 'venw.equipment_id',
                                    need_wash: 'venw.need_wash',
                                    wash_id: 'venw.wash_id',
                                },
                                from: 's:view_equipment_need_washing',
                                from_as: 'venw',
                                where: {
                                    $in: ['venw.equipment_id', {$: state.equipments.map((item: any) => item.equipment_id)}]
                                }
                            }),
                        ]).then((result: ServerResult) => {
                            const data = result.getOperation('CHECK_NEEW_WASHING').getContent().documents.filter((item: any) => {
                                return item.need_wash !== 'far'
                            })
                            commit('LOADED_SUCCESS');
                            resolve(data);
                        })
                    })
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    addItem({ state }, {storage_id}) {
        state.storages.push({storage_id: storage_id})
    },

    setEquipments({ commit }, equipments) {
        commit('SET_EQUIPMENTS', equipments)
    },

    changeEqupmentValue({ state }, {idx, active}) {
        if (state.equipments[idx]) {
            state.equipments[idx].active = active
        }
    },

    removeItem({ state }, index: number) {
        if (state.storages[index].id) {
            state.targetForDelete.push(state.storages[index].id)
        }

        state.storages.splice(index, 1);
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    changeStorageValue({ state }, { index, field, newValue}) {
        const _sum = state.storages.reduce((prev: any, item: any, idx) => {
            return prev + (idx == index ? 0 : parseInt(item.quantity))
        }, 0)
        switch (field) {
            case 'quantity':
                newValue = parseInt(newValue)
                if (_sum + newValue > state.form.quantity) {
                    newValue = state.form.quantity - _sum;
                }
                state.storages[index][field] = newValue;
                break

            default:
                state.storages[index][field] = newValue;
        }
    },

    addAttachments({state, dispatch}, {newValue}) {
      state.form.attachments = [
        newValue,
        ...(state.form.attachments ? state.form.attachments : []),
      ]
    },

};

export const mutations: MutationTree<ShiftVDPFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_SHIFT_VDP_DATA(state, {document}) {
        state.document = document;
    },
    SET_EQUIPMENTS(state, equipments) {
        state.equipments = Object.values(equipments.reduce((agg: any, equip: any) => {
            const _ret: any = {
                equipment_id: equip.equipment_id,
                name: equip.name,
            }
            const _acitve: any[] = state.equipments.filter((item: any) => {
                return item.equipment_id == equip.equipment_id
            })
            _ret.active = _acitve.length > 0
            _ret.id = _acitve.length > 0 ? _acitve[0].id : null
            agg[equip.equipment_id] = _ret
            return agg
        }, {}))
    },

    SET_FORM_DATA(state, {document, schema, equipments, full_schema, full_document}) {
        state.document = document;
        state.schema = schema;
        state.full_schema = full_schema;
        state.equipments = equipments;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
        state.full_form = generateFormObject(full_schema, full_document);
    },

    GENERATE_FORM_DATA(state, {schema, full_schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.full_schema = full_schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
        state.full_form = generateFormObject(full_schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ShiftVDPFormState, {field, newValue}) {
        const schema_field = state.schema.fields.filter((item: any) => {
            return item.code == field
        })[0]

        const calc = (form: any) => {
            console.log(form.weight_brutto, form.quantity, form.weight_palett, form.container_tare_weight)
            if (form.quantity && form.weight_brutto && form.weight_palett) {
                form.weight_neto = parseFloat(form.weight_brutto) - (parseFloat(form.quantity) * parseFloat(form.container_tare_weight)) - parseFloat(form.weight_palett);
            }
        }

        if (schema_field && (schema_field.type == 'decimal' || schema_field.type == 'integer')) {
            console.log('newValue', newValue)
            newValue = parseFloat(newValue.replace(',', '.'))
            console.log('newValue', newValue)
        }
        switch (field) {
            case 'quantity':
                if (newValue > 50) {
                    newValue = 50;
                }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'weight_palett':
                if (newValue > 30) {
                    newValue = 30;
                }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'weight_brutto':
                if (newValue > 500) {
                    newValue = 500;
                }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'plan_weight':
                state.form[field] = newValue;
                state.form.deviant_weight = numeral(parseFloat(state.form.plan_weight) - parseFloat(state.form.weight_neto)).format('0.00')
                break

            case 'number':
                state.form[field] = newValue;
                state.full_form[field] = newValue;
                break;

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ShiftVDPFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_shift_vdp_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
